// Emteria Update app `versionCode` follows Android PackageInfo semantics: major*10_000_000 + minor*100_000 + patch*1000
// (e.g. 5.1.0 → 50100000). install-latest uses UpdateService only from this build onward.
const emteriaUpdateMinVersionCodeForService = 50100000;

const meetsEmteriaUpdateServiceThreshold = (versionCode: number): boolean => versionCode >= emteriaUpdateMinVersionCodeForService;

type Extra = {
    key: string;
    type: "string" | "bool";
    value: string;
};

type OsUpdateIntent = {
    component: string;
    action?: string;
    extras?: Extra[];
};

export type StartOsUpdateCommandPayloadInput = {
    caption: string;
    command: string;
    action: string;
    intent: OsUpdateIntent;
    version?: string;
};

type StartOsUpdateCommandPayloadResult = {
    caption: string;
    command: string;
    intent: OsUpdateIntent;
};

type InstallLatestCommandVariant = "update_activity" | "update_service";

type InstallLatestCommandContent = {
    variant: InstallLatestCommandVariant;
    command: "startActivity" | "startService";
    intent: OsUpdateIntent;
};

export const supportsEmteriaUpdateService = (versionCode: number | null): boolean =>
    versionCode !== null && meetsEmteriaUpdateServiceThreshold(versionCode);

export const resolveInstallLatestCommandContent = (
    emteriaUpdateVersionCode: number | null,
): InstallLatestCommandContent => {
    if (!supportsEmteriaUpdateService(emteriaUpdateVersionCode)) {
        return {
            variant: "update_activity",
            command: "startActivity",
            intent: {
                component: "com.emteria.update/.ui.UpdateActivity",
                extras: [
                    {
                        key: "autoupdate",
                        type: "bool",
                        value: "true",
                    },
                ],
            },
        };
    }

    return {
        variant: "update_service",
        command: "startService",
        intent: {
            component: "com.emteria.update/.UpdateService",
            action: "start_update",
        },
    };
};

export const buildStartOsUpdateCommandPayload = (
    commandPayload: StartOsUpdateCommandPayloadInput,
    emteriaUpdateVersionCode: number | null = null,
): StartOsUpdateCommandPayloadResult => {
    const { version = "", action, intent, ...newPayload } = commandPayload;

    if (action === "installLatest") {
        const delivery = resolveInstallLatestCommandContent(emteriaUpdateVersionCode);
        return {
            ...newPayload,
            command: delivery.command,
            intent: delivery.intent,
        };
    }

    const versionedActionMap: Record<string, string> = {
        downloadSpecific: "download_update",
        installSpecific: "start_update",
    };

    if (action in versionedActionMap) {
        return {
            ...newPayload,
            command: "startService",
            intent: {
                component: "com.emteria.update/.UpdateService",
                action: versionedActionMap[action],
                extras: [{ key: "version", type: "string", value: version }],
            },
        };
    }

    return {
        ...newPayload,
        intent,
    };
};
